"use client";

import {useCallback, useEffect, useRef, useState} from "react";

export type UseResizablePanelOptions = {
   /** Smallest width the panel itself can be dragged to. */
   minWidth: number;
   /** Panel grows no larger than `viewport * maxViewportFraction`. */
   maxViewportFraction: number;
   /** Initial width on mount, before the clamp runs. */
   defaultWidth: number;
   /**
    * Smallest allowed width for the opposite column. When set, the panel is
    * additionally clamped to `viewport - minCounterpartWidth` so the chat
    * never collapses below a usable size on small desktops.
    */
   minCounterpartWidth?: number;
   /** Keyboard step size. Defaults to 24px. */
   step?: number;
   /**
    * Which screen edge the panel hugs. Flips drag and arrow-key direction:
    * a right-anchored panel grows when dragging left, a left-anchored one
    * grows when dragging right. Defaults to "right".
    */
   anchor?: "left" | "right";
};

export type UseResizablePanelResult = {
   width: number;
   /** Upper bound used today; exposed so callers can wire `aria-valuemax`. */
   maxWidth: number;
   isDragging: boolean;
   handlePointerDown: (e: React.PointerEvent) => void;
   handleKeyDown: (e: React.KeyboardEvent) => void;
};

const computeMaxWidth = (
   viewport: number,
   maxViewportFraction: number,
   minCounterpartWidth?: number
): number => {
   const fraction = viewport * maxViewportFraction;
   if (minCounterpartWidth === undefined) return fraction;
   return Math.min(fraction, Math.max(0, viewport - minCounterpartWidth));
};

const clamp = (value: number, min: number, max: number): number =>
   Math.min(Math.max(value, min), max);

/**
 * Width state + drag/keyboard behavior for an edge-anchored resizable panel.
 *
 * - Pointer drag (mouse, touch and pen) is rAF-throttled so a pointermove
 *   burst can only trigger one state update per frame; the rest of the layout
 *   (which may host a heavy PDF or Excel preview) no longer re-renders on
 *   every pixel of drag.
 * - Viewport resize re-clamps so a previously-wide panel still fits.
 * - Keyboard: the arrow pointing away from the anchored edge grows the panel
 *   (mirrors the drag direction), the other one shrinks it.
 * - SSR-safe: no window access during initial render.
 */
export const useResizablePanel = ({
   minWidth,
   maxViewportFraction,
   defaultWidth,
   minCounterpartWidth,
   step = 24,
   anchor = "right",
}: UseResizablePanelOptions): UseResizablePanelResult => {
   const [width, setWidth] = useState<number>(defaultWidth);
   const [maxWidth, setMaxWidth] = useState<number>(defaultWidth);
   const [isDragging, setIsDragging] = useState(false);
   const dragState = useRef<{startX: number; startWidth: number} | null>(null);
   const rafRef = useRef<number | null>(null);
   const pendingXRef = useRef<number | null>(null);

   useEffect(() => {
      const recompute = () => {
         const max = computeMaxWidth(
            window.innerWidth,
            maxViewportFraction,
            minCounterpartWidth
         );
         setMaxWidth(max);
         setWidth((w) => clamp(w, minWidth, Math.max(minWidth, max)));
      };
      recompute();
      window.addEventListener("resize", recompute);
      return () => window.removeEventListener("resize", recompute);
   }, [minWidth, maxViewportFraction, minCounterpartWidth]);

   const handlePointerDown = useCallback(
      (e: React.PointerEvent) => {
         if (e.button !== 0) return;
         e.preventDefault();
         // Keeps the drag alive when a touch or pen leaves the thin handle.
         e.currentTarget?.setPointerCapture?.(e.pointerId);
         dragState.current = {startX: e.clientX, startWidth: width};
         setIsDragging(true);
      },
      [width]
   );

   const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
         if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
         e.preventDefault();
         const max = computeMaxWidth(
            window.innerWidth,
            maxViewportFraction,
            minCounterpartWidth
         );
         // The arrow pointing away from the anchored edge grows the panel,
         // mirroring the drag direction for that anchor.
         const growKey = anchor === "left" ? "ArrowRight" : "ArrowLeft";
         const delta = e.key === growKey ? step : -step;
         setWidth((w) => clamp(w + delta, minWidth, Math.max(minWidth, max)));
      },
      [minWidth, maxViewportFraction, minCounterpartWidth, step, anchor]
   );

   useEffect(() => {
      if (!isDragging) return;

      const flush = () => {
         rafRef.current = null;
         const x = pendingXRef.current;
         const state = dragState.current;
         if (x === null || !state) return;
         pendingXRef.current = null;
         const max = computeMaxWidth(
            window.innerWidth,
            maxViewportFraction,
            minCounterpartWidth
         );
         // Dragging away from the anchored edge grows the panel: left for a
         // right-anchored panel, right for a left-anchored one.
         const delta = anchor === "left" ? x - state.startX : state.startX - x;
         setWidth(clamp(state.startWidth + delta, minWidth, Math.max(minWidth, max)));
      };

      const onMove = (e: PointerEvent) => {
         pendingXRef.current = e.clientX;
         if (rafRef.current !== null) return;
         rafRef.current = window.requestAnimationFrame(flush);
      };

      const onUp = () => {
         dragState.current = null;
         setIsDragging(false);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
      // Suppress text selection + swap the cursor for the whole body during
      // drag so the resize feel doesn't break when the pointer crosses
      // children with their own cursor/selection styles.
      const prevSelect = document.body.style.userSelect;
      const prevCursor = document.body.style.cursor;
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";

      return () => {
         window.removeEventListener("pointermove", onMove);
         window.removeEventListener("pointerup", onUp);
         window.removeEventListener("pointercancel", onUp);
         if (rafRef.current !== null) {
            window.cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
         }
         pendingXRef.current = null;
         document.body.style.userSelect = prevSelect;
         document.body.style.cursor = prevCursor;
      };
   }, [isDragging, minWidth, maxViewportFraction, minCounterpartWidth, anchor]);

   return {width, maxWidth, isDragging, handlePointerDown, handleKeyDown};
};
